<template>
    <div class="apprentice-simple-card">
        <design-2021-organization-assignment-show-header-component
            :organization-in="organizationIn"
            :selected-folder="selectedFolder"
            :page-title="pageTitle"
            @add-assignment="addAssignment"
            @add-folder="addFolder">
        </design-2021-organization-assignment-show-header-component>

        <div class="organization-management">
            <b-tabs
                class="apprentice-pills-tab organization-tab"
                v-model="activeTab"
                pills
                vertical
                nav-class="organization-tab-nav"
                content-class="col-12 col-md-8 col-xl-9 organization-folder-tab-content"
                nav-wrapper-class="col-12 col-md-4 col-xl-3"
                @input="updateTabIndex"
                @changed="changeTab">
                <template v-slot:tabs-start>
                    <div class="component-title">
                        <h5 class="title"><i class="fa fa-fw fa-folders"></i>{{ $t('organization.maps') }}</h5>
                    </div>

                    <div
                        class="not-found with-header empty-profile-list"
                        v-if="activeTab === -1">
                        <div
                            class="btn-add-circle icon"
                            v-b-modal="'organization-assignment-show-create-folder-modal'"></div>
                        <div class="alert-notice">
                            {{ $t('organization.no_folders_created_for_this_organization') }}
                        </div>
                    </div>
                </template>

                <b-tab
                    v-for="folder in organization.assignment_folders"
                    :title="folder.name"
                    @click="selectedFolder = folder.id"
                    :key="folder.id">
                    <template v-slot:title>
                        <span class="title">{{ folder.name }}</span>
                        <div class="context-nav organization-folder-context">
                            <b-dropdown
                                class="ellipsis-nav organization-folder-nav"
                                no-caret
                                variant="primary"
                                right
                                toggle-class="organization-folder-context-menu">
                                <template v-slot:button-content>
                                    <i class="fas fa-ellipsis-v"></i>
                                </template>
                                <b-dropdown-item
                                    class="btn-edit edit-organization-folder"
                                    v-b-tooltip="$t('common.edit')"
                                    @click.prevent.stop="showEditFolderModal(folder.id)">
                                    {{ $t('organization.edit_map') }}
                                </b-dropdown-item>
                                <b-dropdown-item
                                    class="remove-organization-folder"
                                    v-b-tooltip="$t('common.delete')"
                                    @click.prevent.stop="removeFolder(folder.id)">
                                    <i class="btn-remove"></i>{{ $t('organization.edit_map') }}
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </template>

                    <div class="assignments">
                        <div
                            class="row"
                            v-if="folder.assignments.length">
                            <div
                                class="col-12 col-md-6 col-lg-4 assignment"
                                v-for="assignment in folder.assignments"
                                :key="assignment.id">
                                <div
                                    class="fa-3x"
                                    v-if="`loading-${assignment.id}` === loadingAssignment">
                                    <i class="fas fa-spinner fa-pulse"></i>
                                </div>

                                <b-card
                                    class="remove-box-shadow assignment-card"
                                    no-body>
                                    <div class="assignment-header">
                                        <b-card-img :src="getImageUrl(assignment)"> </b-card-img>
                                        <div class="actions">
                                            <div class="assignment-share">
                                                <b-button
                                                    class="btn-show btn-share-assignment"
                                                    id="share-toggle"
                                                    v-b-tooltip.hover
                                                    :variant="
                                                        assignment.ab_assignment && assignment.ab_assignment.active
                                                            ? 'success-icon'
                                                            : 'danger-icon'
                                                    "
                                                    @click="showShareAssignmentModal(assignment)"
                                                    :title="
                                                        assignment.ab_assignment && assignment.ab_assignment.active
                                                            ? $t('organization.withdraw_publication')
                                                            : $t('organization.publish_exercise')
                                                    ">
                                                </b-button>
                                            </div>
                                            <div
                                                class="assignment-video-preview"
                                                v-if="
                                                    assignment.ab_assignment &&
                                                    assignment.ab_assignment.hasOwnProperty('video') &&
                                                    assignment.ab_assignment.video !== null
                                                ">
                                                <b-button
                                                    class="btn-play btn-video-preview"
                                                    variant="primary-icon"
                                                    @click="showAssignmentDescriptionModal(assignment.ab_assignment)"
                                                    :v-b-tooltip="$t('organization.view_video')">
                                                </b-button>
                                            </div>

                                            <div class="edit-assignment">
                                                <b-button
                                                    class="btn-edit btn-edit-assignment"
                                                    variant="primary-icon"
                                                    @click="showEditAssignmentModal(assignment)"
                                                    :v-b-tooltip="$t('organization.edit_exercise')">
                                                </b-button>
                                            </div>

                                            <div class="remove-assignment">
                                                <b-button
                                                    class="btn-remove btn-remove-assignment"
                                                    variant="danger-icon"
                                                    @click="removeAssignment(assignment.id)"
                                                    :v-b-tooltip="$t('organization.delete_exercise')">
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                    <b-card-body>
                                        <div class="assignment-title">
                                            <b-card-title>
                                                {{ assignment.name }}
                                            </b-card-title>
                                        </div>

                                        <b-card-text
                                            class="assignment-picker-count"
                                            v-if="assignment.ab_assignment"
                                            text-tag="div">
                                            {{ $t('organization.amount_of_registrations') }}
                                            {{
                                                assignment.ab_assignment.ab_assignments_pick_counter_count
                                                    ? assignment.ab_assignment.ab_assignments_pick_counter_count
                                                    : 0
                                            }}
                                        </b-card-text>

                                        <b-card-text
                                            class="organization-education-levels"
                                            v-if="assignment.ab_assignment && assignment.ab_assignment.active"
                                            text-tag="div">
                                            {{
                                                assignment.ab_assignment.education_levels
                                                    .map((educationLevel) => educationLevel.name)
                                                    .join(' | ')
                                            }}
                                        </b-card-text>

                                        <b-card-text
                                            class="organization-categories"
                                            v-if="assignment.ab_assignment && assignment.ab_assignment.active"
                                            text-tag="div">
                                            {{
                                                assignment.ab_assignment.categories
                                                    .map((category) => category.name)
                                                    .join(' | ')
                                            }}
                                        </b-card-text>

                                        <b-card-text
                                            class="organization-description"
                                            v-if="assignment.ab_assignment && assignment.ab_assignment.active"
                                            text-tag="div">
                                            {{ previewDescription(assignment.ab_assignment.description) }}
                                        </b-card-text>

                                        <b-button
                                            class="btn-show-more show-more-desc"
                                            v-if="
                                                assignment.ab_assignment &&
                                                assignment.ab_assignment.active &&
                                                hasMoreToShow(assignment.ab_assignment.description)
                                            "
                                            variant="primary"
                                            @click="showAssignmentDescriptionModal(assignment.ab_assignment)">
                                            {{ $t('organization.show_more') }}
                                        </b-button>
                                    </b-card-body>
                                </b-card>
                            </div>
                        </div>

                        <div
                            class="not-found with-header empty-assignment-list"
                            v-else>
                            <div class="icon">
                                <i class="fas fa-exclamation-circle"></i>
                            </div>
                            <div class="alert-notice">{{ $t('organization.no_exercises_in_this_folder') }}</div>
                        </div>

                        <design-2021-portfolio-edit-share-modal-component
                            :can-paid="true"
                            ref="share-assignment-modal"
                            @update-assignments="sharedAssignmentUpdate"
                            @remove-shared-assignments="removeSharedAssignment">
                        </design-2021-portfolio-edit-share-modal-component>

                        <design-2021-shared-assignment-description-modal-component ref="description-assignment-modal">
                        </design-2021-shared-assignment-description-modal-component>
                    </div>

                    <design-2021-organization-assignment-show-edit-folder-component
                        :folder-in="folder"
                        @update-folder="editFolder">
                    </design-2021-organization-assignment-show-edit-folder-component>
                </b-tab>
            </b-tabs>
        </div>
    </div>
</template>

<script>
export default {
    name: 'design-2021-organization-assignment-show-component',
    props: {
        organizationIn: Object,
        educationLevels: Array,
        coursesIn: Array,
        pageTitle: {
            type: String,
            default() {
                return '';
            },
        },
    },
    data() {
        return {
            activeTab: 0,
            organization: this.organizationIn,
            selectedFolder: null,
            categories: [],
            loadingAssignment: '',
        };
    },

    methods: {
        previewDescription(value) {
            const dummyDiv = document.createElement('div');
            dummyDiv.innerHTML = value;
            const contentStripped = dummyDiv.textContent || dummyDiv.innerText || '';
            dummyDiv.remove();
            return contentStripped.split(' ').length > 20
                ? contentStripped.split(' ').splice(0, 20).join(' ') + ' ...'
                : contentStripped;
        },

        hasMoreToShow(value) {
            const dummyDiv = document.createElement('div');
            dummyDiv.innerHTML = value;
            const contentStripped = dummyDiv.textContent || dummyDiv.innerText || '';
            dummyDiv.remove();
            return contentStripped.split(' ').length > 20;
        },

        changeTab(currentTabs, previousTabs) {
            if (currentTabs.length > previousTabs.length && previousTabs.length !== 0) {
                this.updateTabIndex(currentTabs.length - 1);
            } else {
                this.updateTabIndex(0);
            }
        },

        updateTabIndex(tabIndex) {
            const name = 'organization-show-' + this.organizationIn.id;
            this.activeTab = tabIndex;
            localStorage.setItem(name, tabIndex);

            if (tabIndex >= 0) {
                this.selectedFolder = this.organization.assignment_folders[tabIndex].id;
            }
        },

        showEditFolderModal(folderId) {
            this.$bvModal.show(`organization-assignment-edit-folder-${folderId}`);
        },

        addFolder(folder) {
            this.organization.assignment_folders.push(folder);
            this.selectedFolder = folder.id;
            this.updateTabIndex(this.organization.assignment_folders.length - 1);
        },

        editFolder(folderId, folderName) {
            const data = {
                name: folderName,
                order: 0,
            };

            this.axios.post(this.routes.organizationAssignmentFolderUpdate(folderId), data).then(
                (response) => {
                    FolderLoop: for (const folder of this.organization.assignment_folders) {
                        if (folder.id === this.selectedFolder) {
                            folder.name = folderName;
                            break FolderLoop;
                        }
                    }
                },

                (error) => {
                    return this.alert.ajaxError(error);
                },
            );
        },

        removeFolder(folderId) {
            let hasChildren = false;

            for (const [needle, folder] of this.organization.assignment_folders.entries()) {
                if (folder.id === folderId) {
                    if (this.organization.assignment_folders[needle].assignments.length >= 1) {
                        hasChildren = true;
                        break;
                    }
                }
            }

            if (hasChildren) {
                this.alert.notice(
                    this.$t('organization.delete_folder'),
                    this.$t(
                        'organization.the_folder_cannot_be_deleted_because_there_are_still_assignments_linked_to_the_folder_move_this_before_deleting_the_folder',
                    ),
                );
            } else {
                this.alert
                    .confirm(
                        this.$t('organization.delete_map'),
                        this.$t('organization.are_you_sure_you_want_to_delete_this_map'),
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        }

                        this.alert.saving();
                        this.axios.delete(this.routes.organizationAssignmentFolderRemove(folderId)).then(
                            (response) => {
                                this.organization.assignment_folders = this.organization.assignment_folders.filter(
                                    (folder) => {
                                        return folder.id !== folderId;
                                    },
                                );

                                this.alert.success();
                            },
                            (error) => {
                                return this.alert.ajaxError(error);
                            },
                        );
                    });
            }
        },

        showShareAssignmentModal(assignment) {
            this.$refs['share-assignment-modal'][0].show(assignment, true);
        },

        showEditAssignmentModal(assignment) {
            window.location.href = `/organization/assignment/${assignment.id}`;
        },

        addAssignment(assignment) {
            let organization = { ...this.organization };
            organization.assignment_folders = this.organization.assignment_folders.map((folder) => {
                if (folder.id === this.selectedFolder) {
                    let new_folder = Object.assign({}, folder);
                    new_folder.assignments.push(assignment);
                    return new_folder;
                }
                return folder;
            });
        },

        removeAssignment(id) {
            this.alert
                .confirm(
                    this.$t('organization.delete_exercise'),
                    this.$t('organization.are_you_sure_you_want_to_delete_this_exercise'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.axios.delete(this.routes.organizationAssignmentRemove(id)).then(
                        (response) => {
                            assignmentLoop: for (const [
                                needle,
                                folder,
                            ] of this.organization.assignment_folders.entries()) {
                                if (folder.id === this.selectedFolder) {
                                    this.organization.assignment_folders[needle].assignments =
                                        this.organization.assignment_folders[needle].assignments.filter(
                                            (assignment) => {
                                                return assignment.id !== id;
                                            },
                                        );
                                    break assignmentLoop;
                                }
                            }

                            this.alert.success();
                        },
                        (error) => {
                            return this.alert.ajaxError(error);
                        },
                    );
                });
        },

        editAssignment(id, assignmentName) {
            const data = {
                name: assignmentName,
            };

            this.axios.post(this.routes.organizationAssignmentUpdate(id), data).then(
                (response) => {
                    FolderLoop: for (const folder of this.organization.assignment_folders) {
                        if (folder.id === this.selectedFolder) {
                            for (const assignment of folder.assignments) {
                                if (assignment.id === id) {
                                    assignment.name = assignmentName;
                                    break FolderLoop;
                                }
                            }
                        }
                    }
                },

                (error) => {
                    return this.alert.ajaxError(error);
                },
            );
        },

        getAllCategories() {
            axios.get(this.routes.assignmentGetCategoriesByCourse(this.organizationIn.id)).then(
                (response) => {
                    if (response.data.length) {
                        this.categories = response.data.map((category) => {
                            return {
                                text: category.name,
                                value: category.id,
                            };
                        });

                        this.categories.unshift({
                            text: this.$t('organization.choose_category'),
                            value: null,
                            disabled: true,
                        });
                    }
                },
                (error) => {
                    this.alert.ajaxError(error, this.$t('organization.error_during_collecting_category'));
                },
            );
        },

        sharedAssignmentUpdate(ab_assignment) {
            this.loadingAssignment = `loading-${ab_assignment.id}`;
            this.organization.assignment_folders = this.organization.assignment_folders.map((folder) => {
                folder.assignments = folder.assignments.map((assignment) => {
                    if (assignment.id === ab_assignment.assignment_id) {
                        assignment.ab_assignment = ab_assignment;
                    }
                    this.loadingAssignment = '';
                    return assignment;
                });
                this.loadingAssignment = '';
                return folder;
            });
        },

        removeSharedAssignment(assignmentId) {
            this.organization.assignment_folders = this.organization.assignment_folders.map((folder) => {
                folder.assignments = folder.assignments.map((assignment) => {
                    if (assignment.id === assignmentId) {
                        assignment.ab_assignment.active = 0;
                    }
                    return assignment;
                });
                return folder;
            });
        },

        showAssignmentDescriptionModal(assignment) {
            let data = {};
            if (assignment.video && assignment.video !== null) {
                data = {
                    title: assignment.name,
                    description: assignment.description,
                    video: {
                        file: {
                            extension: assignment.video.file.extension,
                            full_name: assignment.video.file.full_name,
                            public_path: assignment.video.file.public_path,
                            type: `video/${assignment.video.file.extension}`,
                        },
                    },
                };
            } else {
                data = {
                    title: assignment.name,
                    description: assignment.description,
                };
            }

            this.$refs['description-assignment-modal'][0].show(data);
        },
        getImageUrl(assignment) {
            if (assignment.ab_assignment && assignment.ab_assignment.active) {
                if (assignment.ab_assignment.banner_image.file.image) {
                    return assignment.ab_assignment.banner_image.file.image.thumb_path;
                } else if (assignment.ab_assignment.banner_image.file) {
                    return assignment.ab_assignment.banner_image.file.public_path;
                }
            }

            return '/images/dashboard-background.jpg';
        },
    },

    mounted() {
        const name = 'organization-show-' + this.organizationIn.id;

        if (localStorage.getItem(name)) {
            this.activeTab = parseInt(localStorage.getItem(name));
            if (this.activeTab >= 0 && this.organization.assignment_folders[this.activeTab]) {
                this.selectedFolder = this.organization.assignment_folders[this.activeTab].id;
            }
        } else if (this.organizationIn.assignment_folders.length) {
            this.activeTab = 0;
            this.selectedFolder = this.organization.assignment_folders[0].id;
        } else {
            this.activeTab = -1;
        }

        this.getAllCategories();
    },
};
</script>

<style scoped lang="scss">
::v-deep .organization-tab-nav {
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');

    .component-title {
        background-color: $primary;
        padding: map-get($grid-gutter-spacing, 'fifteen');
        color: $white;
        border-radius: $border-radius-content;
        margin-bottom: map-get($grid-gutter-spacing, 'ten');

        i {
            color: $white;
            margin-right: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .nav-item {
        &:first-child {
            .nav-link {
                padding-top: 0;
            }
        }

        &:not(:last-child) {
            border-bottom: 3px dotted $border-color;
        }
    }

    .nav-link {
        padding:
            map-get($grid-gutter-spacing, 'ten') $grid-gutter-width * 0,
            666666667;
        display: flex;
        align-items: center;
        flex-direction: row;
        color: $text-color;
        font-weight: $font-weight-normal;

        .title {
            max-width: calc(100% - 37px);
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }

        .organization-folder-context {
            margin-left: auto;
            align-self: flex-start;
        }

        &.active {
            color: $text-color;
        }

        &:hover {
            background-color: $info;
            color: $white;
            border-radius: $border-radius-content;
        }
    }
}

.organization-folder-nav ::v-deep {
    .organization-folder-context-menu {
        padding: map-get($grid-gutter-spacing, 'eight');
        line-height: 1;

        i {
            margin-right: 0;
        }
    }

    .dropdown-menu {
        border: 1px solid $border-color;

        li {
            > i,
            &:before {
                min-width: 20px;
            }
        }

        .remove-organization-folder {
            .dropdown-item {
                display: flex;
                align-items: center;
            }
        }
    }
}

.assignment {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.assignment-card {
    border: none;
    height: 100%;

    .assignment-header {
        img {
            height: 100px;
            object-fit: cover;
            object-position: center;
            border-top-left-radius: $border-radius-content;
            border-top-right-radius: $border-radius-content;
        }

        .actions {
            position: absolute;
            top: 8px;
            right: 15px;
            display: flex;
            justify-content: flex-end;
            align-content: baseline;
            flex-direction: row;
            margin: 0;
            width: calc(100% - 30px);

            > div {
                &:not(:last-child) {
                    margin-right: map-get($grid-gutter-spacing, 'ten');
                }
            }

            .btn {
                font-size: $base-font-size;
                width: 34px;
                height: 30px;

                &:before {
                    margin-right: 0;
                }
            }

            .published {
                align-self: center;
            }

            .publish-status {
                display: flex;
                cursor: default;
                font-size: 1.125rem;

                &:hover {
                    color: inherit;
                }
            }
        }
    }

    .card-body {
        display: flex;
        flex-direction: column;
        padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');
        text-align: left;
        border-left: 1px solid $border-color;
        border-right: 1px solid $border-color;
        border-bottom: 1px solid $border-color;
        border-bottom-right-radius: $border-radius-content;
        border-bottom-left-radius: $border-radius-content;

        .assignment-title {
            min-height: 35px;
            margin-bottom: 10px;

            .assignment-card .card-title {
                margin-bottom: 0;
            }
        }

        .assignment-picker-count,
        .organization-categories,
        .organization-description,
        .organization-education-levels {
            color: $text-muted-color;
            font-size: 0.875rem;
            line-height: 1;
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
        }

        .organization-categories {
            min-height: 28px;
        }

        .organization-description {
            height: 47px;
            line-height: 1.2;
            overflow: hidden;
        }

        .show-more-desc {
            display: flex;
            justify-content: flex-end;
            padding: 2px 0;
            font-size: 0.8125rem;
        }
    }
}
</style>
